/**
 * Shared React Query keys.
 *
 * Centralising keys here ensures every caller of the same dataset uses
 * an identical key tuple, so React Query can dedupe in-flight requests
 * and share cached results across components / hooks.
 *
 * Pair with sensible `staleTime` values in the hook itself — these
 * datasets change infrequently and don't need to refetch on every mount.
 */
export const queryKeys = {
  /** Per-user onboarding row from `user_onboarding`. */
  userOnboarding: (userId: string | undefined) =>
    ["user_onboarding", userId ?? "anon"] as const,

  /** Per-user streak/XP row from `user_streaks`. */
  userStreak: (userId: string | undefined) =>
    ["user_streaks", userId ?? "anon"] as const,

  /** Per-user role rows from `user_roles` (used by AdminGuard etc.). */
  userRole: (userId: string | undefined) =>
    ["user_roles", userId ?? "anon"] as const,
} as const;

/**
 * Conservative cache lifetimes for slow-moving user state.
 * Onboarding + streak rows update at most a few times per session.
 */
export const STALE_TIMES = {
  /** 5 minutes — onboarding state is sticky once a user starts. */
  userOnboarding: 5 * 60 * 1000,
  /** 60 seconds — streaks tick on activity but tolerate brief staleness. */
  userStreak: 60 * 1000,
  /** 5 minutes — role assignments change rarely. */
  userRole: 5 * 60 * 1000,
} as const;
